import { extractRedemptionCoupon } from './bazaarService';
import {
  trackAnalyticsEvent,
  trackRevenuePurchase,
  updateAnalyticsUserProperties,
} from './analyticsService';
import { getUserProfile } from '../storage/userProfileStorage';

const PAYMENT_PROVIDER = 'razorpay';

function toSlug(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function parseRupeeAmount(priceLabel) {
  const match = String(priceLabel || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
  if (!match) return 0;
  return Number(match[1]) || 0;
}

function resolvePriceInr(card) {
  const raw = card?.raw ?? {};

  if (raw.discountedPrice != null && raw.discountedPrice !== '') {
    return Number(raw.discountedPrice) || 0;
  }
  if (raw.price?.value != null && raw.price.value !== '') {
    return Number(raw.price.value) || 0;
  }
  if (raw.androidStorePrice != null && raw.androidStorePrice !== '') {
    return Number(raw.androidStorePrice) || 0;
  }

  return parseRupeeAmount(card?.priceLabel || card?.price);
}

function resolveVoucherBrand(card) {
  const raw = card?.raw ?? {};
  const source =
    raw.source ||
    raw.brand ||
    raw.displayName ||
    card?.brand ||
    card?.title ||
    'voucher';
  return toSlug(source) || 'voucher';
}

function resolveVoucherValueInr(card) {
  const raw = card?.raw ?? {};
  const candidates = [
    raw.voucherValue,
    raw.voucherValueInr,
    raw.faceValue,
    raw.denomination,
    raw.amount,
    raw.value,
    raw.rewardValue,
  ];

  for (const candidate of candidates) {
    const numeric = Number(candidate);
    if (Number.isFinite(numeric) && numeric > 0) return Math.round(numeric);
  }

  const fromTitle = String(card?.title || raw.title || '').match(/₹\s*([\d,]+)/);
  if (fromTitle) return Number(fromTitle[1].replace(/,/g, '')) || 0;

  return 0;
}

function resolveProductType(card, storeTab) {
  if (storeTab === 'rapcoin' || card?.tone === 'coin') return 'rapcoin';
  if (storeTab === 'rapgem' || card?.tone?.includes('gem')) return 'rapgem';
  if (storeTab === 'vip-room' || card?.tone === 'vip') return 'vip room';
  return storeTab || 'rapcoin';
}

function resolveProductId(card) {
  return (
    card?.productId ||
    card?.raw?.androidStoreProductId ||
    card?.raw?.webStoreProductId ||
    card?.raw?.sku ||
    String(card?.raw?._id || card?.raw?.id || card?.id || '')
  );
}

function resolveQuantity(card) {
  const raw = card?.raw ?? {};
  const pkg = raw.package || raw.pkg || {};
  const fromTitle = Number(String(card?.title || '').replace(/\D/g, ''));

  const candidates = [
    pkg.coins,
    pkg.rapCoins,
    pkg.gems,
    pkg.room,
    pkg.roomCount,
    raw.quantity,
    raw.count,
    fromTitle,
  ];

  for (const candidate of candidates) {
    const numeric = Number(candidate);
    if (Number.isFinite(numeric) && numeric > 0) return Math.round(numeric);
  }

  return 1;
}

function resolvePlanName(card) {
  const title = String(card?.title || card?.raw?.displayName || '').toLowerCase();
  if (title.includes('blockbuster')) return 'blockbuster';
  if (title.includes('superhit') || title.includes('super hit')) return 'superhit';
  if (title.includes('hit')) return 'hit';
  return toSlug(title) || 'pass';
}

function resolveBillingPeriod(card) {
  const interval = String(card?.interval || card?.raw?.interval || '').toLowerCase();
  if (interval === 'daily' || interval === 'weekly' || interval === 'monthly') return interval;
  return 'monthly';
}

function resolvePaymentMethod(method) {
  if (!method) return null;
  const normalized = String(method).toLowerCase();
  if (normalized.includes('upi')) return 'upi';
  if (normalized.includes('netbanking') || normalized.includes('net banking')) return 'netbanking';
  if (normalized.includes('wallet')) return 'wallet';
  if (normalized.includes('card') || normalized.includes('credit') || normalized.includes('debit')) {
    return 'card';
  }
  return normalized;
}

function mapErrorReason(error, fallback = 'unknown_error') {
  if (!error) return fallback;
  const message = String(error?.message || error || '').toLowerCase();
  if (!message) return fallback;
  if (message.includes('cancel')) return 'cancelled';
  if (message.includes('stock') || message.includes('unavailable')) return 'out_of_stock';
  if (message.includes('network') || message.includes('timeout')) return 'network_error';
  if (message.includes('declin')) return 'payment_declined';
  if (message.includes('verif')) return 'verification_failed';
  return message.slice(0, 120) || fallback;
}

/** Redeem: user taps (XP) - REDEEM */
export function trackRedeemTap(card, xpBalance) {
  trackAnalyticsEvent('web_bazaar_redeem_tap', {
    voucher_brand: resolveVoucherBrand(card),
    voucher_value_inr: resolveVoucherValueInr(card),
    xp_cost: Number(card?.xpValue) || 0,
    xp_balance: Number(xpBalance) || 0,
  });
}

/** Redeem: user taps CONFIRM & REDEEM */
export function trackRedeemConfirm(card) {
  trackAnalyticsEvent('web_bazaar_redeem_confirm', {
    voucher_brand: resolveVoucherBrand(card),
    voucher_value_inr: resolveVoucherValueInr(card),
    xp_cost: Number(card?.xpValue) || 0,
  });
}

/** Redeem: backend confirms voucher issuance */
export function trackRedeemResult(card, result, error) {
  const success = result?.ok !== false && !error;
  const voucherCode = success ? extractRedemptionCoupon(result) || null : null;

  trackAnalyticsEvent('web_bazaar_redeem_result', {
    voucher_brand: resolveVoucherBrand(card),
    voucher_value_inr: resolveVoucherValueInr(card),
    xp_spent: Number(card?.xpValue) || 0,
    status: success ? 'success' : 'failed',
    voucher_code: voucherCode,
    error_reason: success ? null : mapErrorReason(error || result?.message, 'redeem_failed'),
  });

  if (success) {
    const profile = getUserProfile();
    updateAnalyticsUserProperties({
      totalXP: profile?.totalXP ?? profile?.totalXp,
      xp_balance: profile?.totalXP ?? profile?.totalXp,
    });
  }
}

/** Store pack: user taps pack tile */
export function trackPurchaseTap(card, storeTab) {
  trackAnalyticsEvent('web_bazaar_purchase_tap', {
    product_type: resolveProductType(card, storeTab),
    product_id: resolveProductId(card),
    quantity: resolveQuantity(card),
    price_inr: resolvePriceInr(card),
  });
}

/** Store pack: user taps CONFIRM & BUY */
export function trackPurchaseConfirm(card, storeTab) {
  trackAnalyticsEvent('web_bazaar_purchase_confirm', {
    product_type: resolveProductType(card, storeTab),
    product_id: resolveProductId(card),
    quantity: resolveQuantity(card),
    price_inr: resolvePriceInr(card),
    payment_provider: PAYMENT_PROVIDER,
  });
}

/** Store pack: payment + server credit result */
export function trackPurchaseResult(card, storeTab, outcome = {}) {
  const {
    status = 'failed',
    paymentMethod = null,
    razorpayPaymentId = null,
    razorpayOrderId = null,
    errorReason = null,
  } = outcome;

  const productType = resolveProductType(card, storeTab);
  const productId = resolveProductId(card);
  const quantity = resolveQuantity(card);
  const priceInr = resolvePriceInr(card);
  const method = resolvePaymentMethod(paymentMethod);

  trackAnalyticsEvent('web_bazaar_purchase_result', {
    product_type: productType,
    product_id: productId,
    quantity,
    price_inr: priceInr,
    payment_provider: PAYMENT_PROVIDER,
    payment_method: method,
    status,
    razorpay_payment_id: razorpayPaymentId,
    razorpay_order_id: razorpayOrderId,
    error_reason: status === 'success' ? null : errorReason || 'purchase_failed',
  });

  if (status === 'success' && razorpayPaymentId) {
    trackRevenuePurchase({
      transactionId: razorpayPaymentId,
      value: priceInr,
      currency: 'INR',
      paymentMode: method || PAYMENT_PROVIDER,
      items: [
        {
          item_id: productId,
          item_name: String(card?.title || productId),
          item_category: productType,
          price: priceInr,
          quantity,
        },
      ],
    });

    const profile = getUserProfile();
    updateAnalyticsUserProperties({
      rapCoinBalance: profile?.rapCoinBalance ?? profile?.totalCoins,
      totalGems: profile?.totalGems,
      vipRoom: profile?.vipRoom,
    });
  }
}

/** Pass: user taps pass card */
export function trackPassPurchaseTap(card) {
  trackAnalyticsEvent('web_bazaar_pass_purchase_tap', {
    plan_name: resolvePlanName(card),
    price_inr: resolvePriceInr(card),
    billing_period: resolveBillingPeriod(card),
  });
}

/** Pass: user taps CONFIRM & BUY */
export function trackPassPurchaseConfirm(card) {
  trackAnalyticsEvent('web_bazaar_pass_purchase_confirm', {
    plan_name: resolvePlanName(card),
    price_inr: resolvePriceInr(card),
    billing_period: resolveBillingPeriod(card),
    payment_provider: PAYMENT_PROVIDER,
  });
}

/** Pass: payment + server activate result */
export function trackPassPurchaseResult(card, outcome = {}, wasPremium = false) {
  const {
    status = 'failed',
    paymentMethod = null,
    razorpayPaymentId = null,
    razorpayOrderId = null,
    errorReason = null,
  } = outcome;

  const planName = resolvePlanName(card);
  const priceInr = resolvePriceInr(card);
  const billingPeriod = resolveBillingPeriod(card);
  const method = resolvePaymentMethod(paymentMethod);

  trackAnalyticsEvent('web_bazaar_pass_purchase_result', {
    plan_name: planName,
    price_inr: priceInr,
    billing_period: billingPeriod,
    payment_provider: PAYMENT_PROVIDER,
    payment_method: method,
    status,
    is_renewal: Boolean(wasPremium),
    razorpay_payment_id: razorpayPaymentId,
    razorpay_order_id: razorpayOrderId,
    error_reason: status === 'success' ? null : errorReason || 'purchase_failed',
  });

  if (status === 'success' && razorpayPaymentId) {
    trackRevenuePurchase({
      transactionId: razorpayPaymentId,
      value: priceInr,
      currency: 'INR',
      paymentMode: method || PAYMENT_PROVIDER,
      items: [
        {
          item_id: resolveProductId(card) || planName,
          item_name: String(card?.title || planName),
          item_category: 'pass',
          price: priceInr,
          quantity: 1,
        },
      ],
    });

    const profile = getUserProfile();
    updateAnalyticsUserProperties({
      isPremium: profile?.isPremium ?? true,
      subscription_tier: planName,
      subscription_start_date: profile?.subscriptionStartDate ?? profile?.subscriptionStartedAt,
      subscription_expiry_date:
        profile?.subscriptionExpiresAt ?? profile?.subscriptionEndDate ?? profile?.passExpiresAt,
      subscription_status: profile?.subscriptionStatus || 'active',
    });
  }
}
